import toast from 'react-hot-toast';
import { useTheme } from '../theme';

// Toast utility functions with consistent styling
export const showToast = {
  success: (message: string) => {
    return toast.success(message, {
      duration: 3000,
      position: 'bottom-right',
      style: {
        background: 'var(--semantic-colors-background-card)',
        color: 'var(--semantic-colors-text-primary)',
        border: 'var(--semantic-spacing-interactive-border) solid var(--semantic-colors-status-success)',
        borderRadius: 'var(--primitive-borders-radius-md)',
        fontSize: 'var(--primitive-typography-font-sizes-sm)',
        maxWidth: 'var(--semantic-spacing-layout-content-width-sm)',
      },
      iconTheme: {
        primary: 'var(--semantic-colors-status-success)',
        secondary: 'var(--semantic-colors-background-card)',
      },
    });
  },

  error: (message: string) => {
    return toast.error(message, {
      duration: 4000,
      position: 'bottom-right',
      style: {
        background: 'var(--semantic-colors-background-card)',
        color: 'var(--semantic-colors-text-primary)',
        border: 'var(--semantic-spacing-interactive-border) solid var(--semantic-colors-status-error)',
        borderRadius: 'var(--primitive-borders-radius-md)',
        fontSize: 'var(--primitive-typography-font-sizes-sm)',
        maxWidth: 'var(--semantic-spacing-layout-content-width-sm)',
      },
      iconTheme: {
        primary: 'var(--semantic-colors-status-error)',
        secondary: 'var(--semantic-colors-background-card)',
      },
    });
  },

  loading: (message: string) => {
    return toast.loading(message, {
      position: 'bottom-right',
      style: {
        background: 'var(--semantic-colors-background-card)',
        color: 'var(--semantic-colors-text-primary)',
        border: 'var(--semantic-spacing-interactive-border) solid var(--semantic-colors-border-default)',
        borderRadius: 'var(--primitive-borders-radius-md)',
        fontSize: 'var(--primitive-typography-font-sizes-sm)',
        maxWidth: 'var(--semantic-spacing-layout-content-width-sm)',
      },
    });
  },

  info: (message: string) => {
    return toast(message, {
      duration: 3000,
      position: 'bottom-right',
      icon: '💡',
      style: {
        background: 'var(--semantic-colors-background-card)',
        color: 'var(--semantic-colors-text-primary)',
        border: 'var(--semantic-spacing-interactive-border) solid var(--semantic-colors-interactive-primary)',
        borderRadius: 'var(--primitive-borders-radius-md)',
        fontSize: 'var(--primitive-typography-font-sizes-sm)',
        maxWidth: 'var(--semantic-spacing-layout-content-width-sm)',
      },
    });
  },

  promise: <T>(
    promise: Promise<T>,
    {
      loading,
      success,
      error,
    }: {
      loading: string;
      success: string | ((value: T) => string);
      error: string | ((err: any) => string);
    }
  ) => {
    return toast.promise(promise, 
      { loading, success, error },
      {
        position: 'bottom-right',
        style: {
          background: 'var(--semantic-colors-background-card)',
          color: 'var(--semantic-colors-text-primary)',
          borderRadius: 'var(--primitive-borders-radius-md)',
          fontSize: 'var(--primitive-typography-font-sizes-sm)',
          maxWidth: 'var(--semantic-spacing-layout-content-width-sm)',
        },
        success: {
          style: {
            border: 'var(--semantic-spacing-interactive-border) solid var(--semantic-colors-status-success)',
          },
        },
        error: {
          style: {
            border: 'var(--semantic-spacing-interactive-border) solid var(--semantic-colors-status-error)',
          },
        },
      }
    );
  },

  // Auto-save specific notifications
  autoSave: {
    success: () => showToast.success('Character saved automatically'),
    error: (error?: string) => showToast.error(`Auto-save failed${error ? `: ${error}` : ''}`),
    offline: () => showToast.info('Changes saved locally - will sync when online'),
  },

  // Equipment specific notifications
  equipment: {
    added: (itemName: string) => showToast.success(`Added ${itemName} to inventory`),
    removed: (itemName: string) => showToast.success(`Removed ${itemName} from inventory`),
    equipped: (itemName: string) => showToast.success(`Equipped ${itemName}`),
    unequipped: (itemName: string) => showToast.success(`Unequipped ${itemName}`),
    error: (message: string) => showToast.error(`Equipment error: ${message}`),
  },

  // Character management notifications
  character: {
    created: (name: string) => showToast.success(`Created character: ${name}`),
    loaded: (name: string) => showToast.success(`Loaded character: ${name}`),
    deleted: (name: string) => showToast.success(`Deleted character: ${name}`),
    levelUp: (newLevel: number) => showToast.success(`Level up! Now level ${newLevel}`),
    error: (message: string) => showToast.error(`Character error: ${message}`),
  },

  // Content Library specific notifications
  library: {
    itemDetails: (itemName: string) => showToast.info(`Viewing details: ${itemName}`),
    searchResults: (count: number, query: string) => 
      showToast.info(`Found ${count} results for "${query}"`),
    noResults: (query: string) => showToast.error(`No results found for "${query}"`),
  },

  // Validation and error handling
  validation: {
    required: (fieldName: string) => showToast.error(`${fieldName} is required`),
    invalid: (fieldName: string) => showToast.error(`Invalid ${fieldName}`),
    tooLong: (fieldName: string, maxLength: number) => 
      showToast.error(`${fieldName} must be ${maxLength} characters or less`),
    tooShort: (fieldName: string, minLength: number) => 
      showToast.error(`${fieldName} must be at least ${minLength} characters`),
  },
};

// Dismiss functions
export const dismissToast = (toastId: string) => {
  toast.dismiss(toastId);
};

export const dismissAllToasts = () => {
  toast.dismiss();
};

export default showToast;